import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import { checkAuthStatus } from "./redux/slices/authSlice";
import { DarkModeProvider } from "./contexts/DarkModeContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Components
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import ScrollToTop from "./components/scrollToTop/ScrollToTop";
import ChatBot from "./components/ChatBot/ChatBot";
import EducationalOrangeSpinner from "./components/LoadingSpinner/OrangeSpinner";
import EidMiladBanner from "./components/EidMiladBanner";

// Pages
import Home from "./pages/Home/Home";
import Register from "./pages/register/register";
import RegisterPhone from "./pages/registerPhone/RegisterPhone";
import OTP from "./pages/OTP/OTP";
import RegisterData from "./pages/registerData/registerData";
import AboutUs from "./pages/AboutUs/AboutUs";
import ContactUs from "./pages/ContactUs/ContactUs";
import AllLessons from "./pages/AllLessons/AllLessons";
import AllTeachers from "./pages/AllTeachers/AllTeachers";
import AllStudents from "./pages/AllStudents/AllStudents";
import AllSubjects from "./pages/AllSubjects/AllSubjects";
import AllExams from "./pages/AllExams/AllExams";
import AllQuestions from "./pages/AllQuestions/AllQuestions";
import AllFiles from "./pages/AllFiles/AllFiles";
import CreateTest from "./pages/CreateTest/CreateTest";
import CreateDuty from "./pages/CreateDuty/CreateDuty";
import Library from "./pages/Library/Library";
import Lab from "./pages/Lab/Lab";
import Labs from "./pages/Labs/Labs";
import LabDetails from "./pages/LabDetails/LabDetails";
import LessonDetails from "./pages/LessonDetails/LessonDetails";
import TeacherDetails from "./pages/TeacherDetails/TeacherDetails";
import StartExam from "./pages/StartExam/StartExam";
import PlayWith3almny from "./pages/PlayWith3almny/PlayWith3almny";
import ThreeAlmnyDiscover from "./pages/3almnyDiscover/3almnyDiscover";
import ScheduleTable from "./pages/ScheduleTable/ScheduleTable";
import Login from "./pages/Login/login";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import ExamHistory from "./pages/ExamHistory";
import ExamAnswers from "./pages/ExamAnswers";
import SubjectTeachers from "./pages/SubjectTeachers/SubjectTeachers";
import Profile from "./pages/Profile/Profile";
import CheckCode from "./pages/Profile/CheckCode";
import Competitions from "./pages/Competitions/Competitions";
import DutySolver from "./pages/DutySolver/DutySolver";
import DutyRecords from "./pages/DutyRecords/DutyRecords";
import DutyDetails from "./pages/DutyDetails/DutyDetails";
import LevelIndicator from "./pages/LevelIndicator/LevelIndicator";
import StudentReports from "./pages/StudentReports/StudentReports";
import CreateReport from "./pages/CreateReport/CreateReport";
import ReportDetails from "./pages/ReportDetails/ReportDetails";
import PDFViewer from "./components/PDFViewer/PDFViewer";
import ParentChildLessons from "./components/ParentChildLessons/ParentChildLessons";
import ParentChildReports from "./components/ParentChildReports/ParentChildReports";
import ParentChildTests from "./components/ParentChildTests/ParentChildTests";
import SmartAssistant from "./pages/SmartAssistant/SmartAssistant";
import SmartQuestions from "./pages/SmartQuestions/SmartQuestions";
import ChatWithTeacher from "./pages/ChatWithTeacher/ChatWithTeacher";
import LearnProgramming from "./pages/LearnProgramming/LearnProgramming";

function App() {
  const LOADER_SESSION_KEY = 'initialLoaderShown';
  const [isLoading, setIsLoading] = useState(() => {
    try {
      return sessionStorage.getItem(LOADER_SESSION_KEY) !== 'true';
    } catch (e) {
      return true;
    }
  });

  // Check auth status on app start
  useEffect(() => {
    const initializeApp = async () => {
      try {
        await store.dispatch(checkAuthStatus());
        const hasShown = (() => {
          try { return sessionStorage.getItem(LOADER_SESSION_KEY) === 'true'; } catch { return false; }
        })();
        if (!hasShown) {
          // Show loader only once per session, then mark as shown
          setTimeout(() => {
            try { sessionStorage.setItem(LOADER_SESSION_KEY, 'true'); } catch {}
            setIsLoading(false);
          }, 2000);
        } else {
          // Already shown this session → skip loader immediately
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error initializing app:', error);
        try { sessionStorage.setItem(LOADER_SESSION_KEY, 'true'); } catch {}
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Handle browser back button - redirect to home for protected pages
  useEffect(() => {
    const handlePopState = (event) => {
      // Get current path
      const currentPath = window.location.pathname;
      
      // Protected pages that should redirect to home when back button is pressed
      const protectedPages = ['/Library', '/schedule'];
      
      // If on a protected page and going back, redirect to home
      if (protectedPages.includes(currentPath)) {
        window.history.pushState(null, '', '/');
        window.location.href = '/';
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Routes that don't need Navbar/Footer
  const authRoutes = [
    "/login",
    "/register",
    "/registerPhone",
    "/otp",
    "/registerData",
    "/reset-password",
  ];

  // عرض اللودر أثناء التحميل
  if (isLoading) {
    return (
      <Provider store={store}>
        <DarkModeProvider>
          <EducationalOrangeSpinner />
        </DarkModeProvider>
      </Provider>
    );
  }

  return (
    <Provider store={store}>
      <DarkModeProvider>
        <BrowserRouter>
          <div className="App">
            <ToastContainer
              position="top-center"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={true}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            <Routes>
              {authRoutes.map((route) => (
                <Route
                  key={route}
                  path={route}
                  element={
                    <>
                      <EidMiladBanner />
                      {route === "/login" && <Login />}
                      {route === "/register" && <Register />}
                      {route === "/registerPhone" && <RegisterPhone />}
                      {route === "/otp" && <OTP />}
                      {route === "/registerData" && <RegisterData />}
                      {route === "/reset-password" && <ResetPassword />}
                    </>
                  }
                />
              ))}

              <Route
                path="/"
                element={
                  <>
                    <EidMiladBanner />
                    <Navbar />
                    <Home />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/smart-assistant"
                element={
                  <>
                    <EidMiladBanner />
                    <Navbar />
                    <SmartAssistant />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/smart-questions"
                element={
                  <>
                    <Navbar />
                    <SmartQuestions />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/chat"
                element={
                  <>
                    <Navbar />
                    <ChatWithTeacher />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/learn-programming"
                element={
                  <>
                    <Navbar />
                    <LearnProgramming />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/about"
                element={
                  <>
                    <Navbar />
                    <AboutUs />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/contact"
                element={
                  <>
                    <Navbar />
                    <ContactUs />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/lessons"
                element={
                  <>
                    <Navbar />
                    <AllLessons />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/parent-child-lessons"
                element={
                  <>
                    <Navbar />
                    <ParentChildLessons />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/parent-child-reports"
                element={
                  <>
                    <Navbar />
                    <ParentChildReports />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/parent-child-tests"
                element={
                  <>
                    <Navbar />
                    <ParentChildTests />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/teachers"
                element={
                  <>
                    <Navbar />
                    <AllTeachers />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/students"
                element={
                  <>
                    <Navbar />
                    <AllStudents />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/subjects"
                element={
                  <>
                    <Navbar />
                    <AllSubjects />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/exams"
                element={
                  <>
                    <Navbar />
                    <AllExams />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/create-test"
                element={
                  <>
                    <Navbar />
                    <CreateTest />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/create-duty/:lessonId"
                element={
                  <>
                    <Navbar />
                    <CreateDuty />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/questions"
                element={
                  <>
                    <Navbar />
                    <AllQuestions />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/files"
                element={
                  <>
                    <Navbar />
                    <AllFiles />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/Library"
                element={
                  <>
                    <Navbar />
                    <Library />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/pdf-viewer"
                element={
                  <>
                    <Navbar />
                    <PDFViewer />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/labs"
                element={
                  <>
                    <Navbar />
                    <Labs />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/lab"
                element={
                  <>
                    <Navbar />
                    <Lab />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/lab-details"
                element={
                  <>
                    <Navbar />
                    <LabDetails />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/lessonDetails/:id"
                element={
                  <>
                    <Navbar />
                    <LessonDetails key={window.location.pathname} />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/teacherDetails/:id"
                element={
                  <>
                    <Navbar />
                    <TeacherDetails />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/startExam/:id"
                element={
                  <>
                    <Navbar />
                    <StartExam />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/play"
                element={
                  <>
                    <Navbar />
                    <PlayWith3almny />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/discover"
                element={
                  <>
                    <Navbar />
                    <ThreeAlmnyDiscover />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/schedule"
                element={
                  <>
                    <Navbar />
                    <ScheduleTable />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/exam-history"
                element={
                  <>
                    <Navbar />
                    <ExamHistory />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/exam-answers/:testId"
                element={
                  <>
                    <Navbar />
                    <ExamAnswers />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/subject-teachers/:subjectId"
                element={
                  <>
                    <Navbar />
                    <SubjectTeachers />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/profile"
                element={
                  <>
                    <Navbar />
                    <Profile />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/check-code"
                element={
                  <>
                    <Navbar />
                    <CheckCode />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/competitions"
                element={
                  <>
                    <Navbar />
                    <Competitions />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/duty-solver/:dutyId"
                element={
                  <>
                    <Navbar />
                    <DutySolver />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/duty-records/:id"
                element={
                  <>
                    <Navbar />
                    <DutyRecords />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/duty-details/:id"
                element={
                  <>
                    <Navbar />
                    <DutyDetails />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/level-indicator"
                element={
                  <>
                    <Navbar />
                    <LevelIndicator />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/lab/:labId"
                element={
                  <>
                    <Navbar />
                    <Lab />
                    <Footer />
                  </>
                }
              />

              {/* Student Reports Routes */}
              <Route
                path="/student-reports/:studentId/:classId"
                element={
                  <>
                    <Navbar />
                    <StudentReports />
                    <Footer />
                  </>
                }
              />

            

              <Route
                path="/create-report/:studentId/:classId"
                element={
                  <>
                    <Navbar />
                    <CreateReport />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/report-details/:reportId"
                element={
                  <>
                    <Navbar />
                    <ReportDetails />
                    <Footer />
                  </>
                }
              />
            </Routes>
            <ScrollToTop />
            <ChatBot />
          </div>
        </BrowserRouter>
      </DarkModeProvider>
    </Provider>
  );
}

export default App;
